import { createSlice } from "@reduxjs/toolkit";
import * as DatasetAPI from "api/DatasetAPI";
import { getDataset, transformDataset, updateDataset } from "containers/Home/reducer";
import * as RelationUtils from "utils/relation";
import { getSchemaColumns } from "utils/expression";
import { AibleClientFactory } from "client/aible/factory";
export const SECTION_KEYS = [
    "apply",
    "project",
    "unary",
    "date",
    "join",
    "distinct",
    "filter",
    "union",
    "binary",
    "case",
    "pivot",
    "aggregate",
    "sql"
];
const initialState = {};
const relationSlice = createSlice({
    name: "relation",
    initialState: initialState,
    reducers: {
        updateDraft(state, { payload }) {
            return {
                ...state,
                [payload.id]: payload.draft
            };
        },
        deleteDraft(state, { payload }) {
            if (state[payload] === undefined) {
                return state;
            }
            const next = { ...state };
            delete state[payload];
            return next;
        }
    }
});
export const { updateDraft, deleteDraft } = relationSlice.actions;
export default relationSlice.reducer;
export function getSectionDefault(factory, key) {
    switch (key) {
        case "project":
        case "apply":
            return factory.project();
        case "binary":
        case "case":
        case "date":
        case "unary":
            return factory.mutate();
        case "distinct":
            return factory.distinct();
        case "filter":
            return factory.filter();
        case "join":
            return factory.join();
        case "union":
            return factory.union();
        case "pivot":
            return factory.pivot();
        case "sql":
            return factory.sql();
    }
    throw Error(`unknown key: ${key}`);
}
export function getDefaultSections(base) {
    const defaultFactory = RelationUtils.DefaultRelationFactory.from(base);
    return {
        apply: defaultFactory.project(),
        project: defaultFactory.project(),
        unary: defaultFactory.mutate(),
        date: defaultFactory.mutate(),
        join: defaultFactory.join(),
        distinct: defaultFactory.distinct(),
        filter: defaultFactory.filter(),
        union: defaultFactory.union(),
        binary: defaultFactory.mutate(),
        case: defaultFactory.mutate(),
        pivot: defaultFactory.pivot(),
        aggregate: defaultFactory.aggregate(),
        sql: defaultFactory.sql()
    };
}
function getDefaultDraft(dataset) {
    const base = RelationUtils.fromDataset(dataset);
    return {
        name: dataset?.name ?? "",
        base: base,
        target: dataset?.outcome ?? "",
        targetValue: dataset?.target_value?.[0],
        sections: getDefaultSections(base),
        referenceDatasetId: dataset?.id ?? -1
    };
}
function updateDraftBase(draft, update) {
    const currentDefaults = getDefaultSections(draft.base);
    const newDefaults = getDefaultSections(update);
    const sections = SECTION_KEYS.reduce((s, key) => {
        s[key] = RelationUtils.isEqual(s[key], currentDefaults[key])
            ? newDefaults[key]
            : RelationUtils.builder(update).cherryPick(s[key]).build();
        return s;
    }, { ...draft.sections });
    return {
        name: draft.name,
        base: update,
        target: RelationUtils.ensureValidTarget(update, draft.target),
        targetValue: RelationUtils.ensureValidTargetValue(update, draft.target, draft.targetValue),
        sections: sections,
        referenceDatasetId: draft.referenceDatasetId
    };
}
export function getDraft(state, id) {
    if (state.relation[id] !== undefined) {
        return state.relation[id];
    }
    const dataset = getDataset(state, id);
    return getDefaultDraft(dataset);
}
export function getBaseDraft(state, id) {
    return getDraft(state, id).base;
}
export function getSectionDraft(state, id, key) {
    return getDraft(state, id).sections[key];
}
export function updateSectionDraft(id, key, value) {
    return (dispatch, getState) => {
        const current = getDraft(getState(), id);
        const draft = {
            name: current.name,
            base: current.base,
            target: current.target,
            targetValue: current.targetValue,
            sections: {
                ...current.sections,
                [key]: value
            },
            referenceDatasetId: current.referenceDatasetId
        };
        dispatch(updateDraft({ id, draft }));
        return draft;
    };
}
export function updateDraftName(id, name) {
    return (dispatch, getState) => {
        const current = getDraft(getState(), id);
        const draft = {
            name: name,
            base: current.base,
            target: current.target,
            targetValue: current.targetValue,
            sections: current.sections,
            referenceDatasetId: current.referenceDatasetId
        };
        dispatch(updateDraft({ id, draft }));
        return draft;
    };
}
export function updateDraftTarget(id, target) {
    return (dispatch, getState) => {
        const current = getDraft(getState(), id);
        const draft = {
            name: current.name,
            base: current.base,
            target: RelationUtils.ensureValidTarget(current.base, target),
            targetValue: RelationUtils.ensureValidTargetValue(current.base, target, current.targetValue),
            sections: current.sections,
            referenceDatasetId: current.referenceDatasetId
        };
        dispatch(updateDraft({ id, draft }));
        return draft;
    };
}
export function updateDraftTargetValue(id, targetValue) {
    return (dispatch, getState) => {
        const current = getDraft(getState(), id);
        const draft = {
            name: current.name,
            base: current.base,
            target: current.target,
            targetValue: RelationUtils.ensureValidTargetValue(current.base, current.target, targetValue),
            sections: current.sections,
            referenceDatasetId: current.referenceDatasetId
        };
        dispatch(updateDraft({ id, draft }));
        return draft;
    };
}
export function rebaseDraft(id, relation) {
    return (dispatch, getState) => {
        const current = getDraft(getState(), id);
        const draft = updateDraftBase(current, relation);
        dispatch(updateDraft({ id, draft }));
        return draft;
    };
}
export function rebaseDraftOnSection(id, key) {
    return (dispatch, getState) => {
        const current = getDraft(getState(), id);
        const newBase = getSectionDraft(getState(), id, key);
        const newDefaults = getDefaultSections(newBase);
        const draft = updateDraftBase(current, newBase);
        draft.sections[key] = newDefaults[key];
        dispatch(updateDraft({ id, draft }));
        return draft;
    };
}
export function resetDraft(id) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), id);
        const draft = getDefaultDraft(dataset);
        dispatch(updateDraft({ id, draft }));
        return draft;
    };
}
export function resetDraftAndUnstageUpdates(id, referenceDatasetId) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), id);
        const draft = getDefaultDraft(dataset);
        draft.base = RelationUtils.stripComposedTags(draft.base, referenceDatasetId);
        const tag = RelationUtils.getLatestTag(draft.base);
        if (tag !== undefined) {
            draft.referenceDatasetId = tag.dataset_id;
        }
        dispatch(updateDraft({ id, draft }));
        return draft;
    };
}
export function submitDraft(id) {
    return (dispatch, getState) => {
        const current = getDraft(getState(), id);
        dispatch(updateDraft({
            id,
            draft: {
                ...current,
                isSubmitting: true
            }
        }));
        return dispatch(copyAndTransform(id, current.name, current.base, current.target, current.targetValue)).finally(() => dispatch(resetDraft(id)));
    };
}
export function copyAndTransform(id, name, relation, target, targetValue, folderIdOverride) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), id);
        if (dataset === undefined) {
            return Promise.resolve(undefined);
        }
        const transformations = { ...dataset.transformations };
        transformations.plan = {
            relation: RelationUtils.simplify(relation)
        };
        const labels = getSchemaColumns(RelationUtils.getOutputContext(relation));
        return DatasetAPI.copyAndTransform(dataset, transformations, name, target, targetValue, labels, folderIdOverride).then((response) => {
            const t = transformDataset(response);
            dispatch(updateDataset(response.id, t));
            return t;
        });
    };
}
export function getDraftValidatedOutput(relation) {
    return (_, getState) => {
        const tag = RelationUtils.getLatestTag(relation);
        const dataset = getDataset(getState(), tag?.dataset_id ?? -1);
        if (dataset === undefined) {
            return Promise.resolve({
                output_schema: []
            });
        }
        return AibleClientFactory.getInstance()
            .getAccountClient()
            .getCustomerClientFactory(dataset.account_id)
            .then((f) => f.getMetaClient().getValidateRelation(relation));
    };
}
export function getDraftQueryPreview(id) {
    return (_, getState) => {
        const current = getDraft(getState(), id);
        const dataset = getDataset(getState(), id);
        if (dataset === undefined) {
            return Promise.resolve(undefined);
        }
        return AibleClientFactory.getInstance()
            .getAccountClient()
            .getCustomerClientFactory(dataset.account_id)
            .then((f) => f.getMetaClient().getQueryPreview(current.base))
            .then((r) => r.query);
    };
}
